// @react
import React from 'react'
// @types
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @common
import colors from '@bit/atd.web.colors/dist/colors'
import Pluralize from 'react-pluralize'
import strings from 'lib/constants/strings'
import Helper from 'utils/Helper.class'
import { makeStyles } from '@material-ui/core/styles'
// @components
import Tag from '@bit/atd.web.cta.tag'
import Icon from '@bit/atd.web.icons'
import Spacer from '@bit/atd.web.spacer'
import Grid from '@bit/atd.web.grid'
import Lozenge from '@bit/atd.web.lozenge'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
import QueryError from 'modules/common/components/Errors/QueryError'
import useShowMore from 'modules/common/hooks/useShowMore'
import { Typography } from '@material-ui/core'

/**
 * 2.0.0.2 MyATD - Dashboard - Topics
 * UX:https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=866%3A3045
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 * response namespaces: User/Certification, User/Job, User/Certification
 *
 * endpoint: topics
 * response: TopicsListDTO
 * response namespaces: Topics
 */
const useStyles = makeStyles(() => ({
  title: {
    textAlign: 'center',
  },
  tag: {
    textDecoration: 'none',
  },
}))

const Topics = () => {
  const classes = useStyles()
  /******************************************
   * GQL HOOKS
   ******************************************/

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  /******************************************
   * HOOKS
   ******************************************/
  const followedTopics = data?.user?.topics.filter(
    (item) => item.userStatus.followed,
  )

  const { visibleItems, showMoreProps, ShowMore } = useShowMore({
    limit: 5,
    showMoreLimit: 10,
    showMore: true,
    items: followedTopics || [],
  })

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const lozengeText = isLoading ? (
    <></>
  ) : (
    <>
      <span>following </span>
      <Pluralize singular={'topic'} count={followedTopics?.length} />
    </>
  )

  const followingLabel = !isLoading ? (
    // @todo make lozenge support elements
    <Lozenge variant={'subtle'} size={'small'} color={'default'}>
      {/*// @ts-ignore*/}
      {lozengeText}
    </Lozenge>
  ) : (
    <></>
  )

  const topicsContent = followedTopics?.length && (
    <Grid container spacing={1}>
      {visibleItems.map((item) => {
        return (
          <Grid key={`${item.__typename}_${item.key}`} item xs={12}>
            <Tag className={classes.tag} href={item.url}>
              {item.label}
            </Tag>
          </Grid>
        )
      })}

      <Grid item xs={12}>
        <Spacer size={4} fullWidth />
      </Grid>

      <Grid item xs={12}>
        <ShowMore key="showMore" {...showMoreProps} />
      </Grid>
    </Grid>
  )

  const loadedContent = followedTopics?.length ? (
    <Box
      variant="subModule"
      header={{
        title: 'My Topics',
        subtitle: followingLabel,
        actions: (
          <Button
            size={'small'}
            variant="tertiary"
            href={Helper.getPathFromRouteKey('PROFILE_TOPICS')}
          >
            <Icon glyph="edit" size={16} color={colors.ctaPrimary} />
            <Spacer size={8} />
            <span>Edit</span>
          </Button>
        ),
        actionsAlignment: 'right',
      }}
      footer={{
        link: {
          url: Helper.getPathFromRouteKey('PROFILE_TOPICS'),
          label: strings.DASHBOARD.VIEW_ALL_TOPICS,
        },
      }}
    >
      {topicsContent}
    </Box>
  ) : (
    <Box>
      <>
        <Grid item xs={12}>
          <Typography className={classes.title} component={'h3'}>
            Start following topics so they appear here for quick access later.
          </Typography>
        </Grid>

        <Spacer size={16} />

        <Grid item xs={12}>
          <Button
            fullWidth
            alignment={'center'}
            variant="secondary"
            href={Helper.getPathFromRouteKey('PROFILE_TOPICS')}
          >
            Add a topic
          </Button>
        </Grid>
      </>
    </Box>
  )

  return !isLoading ? (
    loadedContent
  ) : (
    <Box
      variant="subModule"
      header={{
        title: 'My Topics',
      }}
    >
      <SkeletonLoader />
    </Box>
  )
}

export default Topics
